import { useCombatStore } from '~/stores/useCombatStore'
import { usePlayerStore } from '~/stores/usePlayerStore'
import { useInventoryStore } from '~/stores/useInventoryStore'
import { useDaycareStore } from '~/stores/useDaycareStore'
import { useAchievements } from '~/composables/useAchievements'
import { useProgressionMoments } from '~/composables/useProgressionMoments'
import { trackProgressionEvent } from '~/composables/useProgressionAnalytics'
import { useAuthStore } from '~/stores/useAuthStore'
import { useToast } from '~/composables/useToast'
import { useAudioManager } from '~/composables/useAudioManager'
import { useTeamSynergy } from '~/composables/useTeamSynergy'
import { useDamageNumbers } from '~/composables/useDamageNumbers'
import { accrueCombatGold } from '~/composables/useCombatGoldCredit'
import { getStartersForGen } from '~/data/starters'
import { getZone } from '~/data/zones'
import {
  BATTLE_TOWER_BOSS_HP,
  BATTLE_TOWER_CAPSTONE_BOSS_HP,
  BATTLE_TOWER_CAPSTONE_ZONE_INDEX,
  BATTLE_TOWER_FARM_WILD_HP,
  BATTLE_TOWER_FARM_ZONE_INDEX,
  BATTLE_TOWER_TYPE_ORDER,
  battleTowerTrialOffenseTypeMult,
  getNextBattleTowerZoneIndex,
  getBattleTowerZone,
  getBattleTowerFarmZone,
  battleTowerFarmUnlocked,
  isPaldeaEndgameFrontierSuperseded,
  scaleBattleTowerTrialBossHp,
} from '~/data/battleTower'
import { getPokemonType, getPokemonTypes, getTypeEffectiveness } from '~/data/types'
import { getSlugGeneration, getShinyRate } from '~/data/gacha'
import {
  getBattleTowerSanctuaryGoldPerKill,
  getBattleTowerTrialBossGold,
  getBattleTowerTrialBossXp,
} from '../../../shared/battle_tower_rewards'
import {
  computeCombatEffectiveDps,
  getPokemonPowerBreakdown,
  resolvePowerBaseSlug,
} from '~/data/pokemonPower'
import { getWildGoldReward } from '~/utils/wildGoldReward'
import { computeZoneBossTotalHp, zoneBossMinTeamDps } from '~/utils/championBoss'
import {
  computePokemonOffensiveDps,
  computeTeamOffensiveDps,
  isBossBattableByTeam,
  type TeamCombatDpsOpts,
} from '~/utils/bossReadiness'
import type { Rarity } from '~/data/gacha'
import type { PokemonType } from '~/data/types'
import type { WildPokemon, BossTrainer } from '~/data/zones'

let initialized = false
/** Évite le repli auto farm juste après un clic « Retour frontière » (boss porte encore affiché). */
let suppressBossAutoRetreatOnce = false
/** Après Fuir en tour : ne pas rouvrir la porte boss / auto-combat au prochain spawn. */
let suppressTowerBossGateOnce = false

const towerTrialPaused = ref(false)

/** Durée de l’anim de mort avant spawn du prochain adversaire. */
const DEATH_ANIM_MS = 550
/** Marge avant qu’un repair considère l’anim comme bloquée (sans re-créditer). */
const DEATH_STUCK_GRACE_MS = 300

let deathAnimTimeout: ReturnType<typeof setTimeout> | null = null
let dyingStartedAt = 0
/** Or/XP déjà attribués pour l’ennemi en cours de mort — empêche un double crédit. */
let killCreditPending = false
/** Fin de séquence (spawn) enregistrée au KO — réutilisée si l’anim est bloquée. */
let onDeathAnimComplete: (() => void) | null = null

function clearDeathAnimationState() {
  if (deathAnimTimeout != null) {
    clearTimeout(deathAnimTimeout)
    deathAnimTimeout = null
  }
  dyingStartedAt = 0
  killCreditPending = false
  onDeathAnimComplete = null
}

export function suppressBossAutoRetreatOnNextSpawn() {
  suppressBossAutoRetreatOnce = true
}

function isBattleTowerBossTrialZone(zoneIndex: number | null | undefined): boolean {
  return (
    zoneIndex != null &&
    zoneIndex >= 1 &&
    zoneIndex <= BATTLE_TOWER_CAPSTONE_ZONE_INDEX
  )
}

function pauseBattleTowerTrial() {
  towerTrialPaused.value = true
  suppressTowerBossGateOnce = true
}

export function clearTowerTrialPause() {
  towerTrialPaused.value = false
}

/** Réinitialise l’état module (logout / changement de compte). */
export function resetCombatLoopModuleState() {
  initialized = false
  suppressBossAutoRetreatOnce = false
  suppressTowerBossGateOnce = false
  towerTrialPaused.value = false
  clearDeathAnimationState()
  resetPendingBossGate()
}

/** Ouvre le sélecteur de routes sans relancer la porte boss au prochain spawn. */
export function pauseTowerTrialForRouteChange() {
  towerTrialPaused.value = true
  suppressTowerBossGateOnce = true
}

import {
  COMBAT_GOLD_REWARD_SCALE,
  KALOS_WILD_GEN_MULT,
  WILD_GOLD_BASE_GEN1,
  WILD_GOLD_BASE_GEN2,
  WILD_GOLD_GEN_EXPONENT,
  WILD_SHINY_GOLD_MULT,
  WILD_SHINY_XP_MULT,
} from '../../../shared/combat_balance'
import { registerCombatDamageTick } from '~/utils/combatDamageTickRegistry'
import { wildGoldFactor } from '~/utils/economyModifiers'

// Anti-AFK: boss gate — player must click "Fight" before boss timer starts
const pendingBoss = ref(false)
/** Boss figé à l’ouverture de la porte — l’UI doit l’utiliser (pas `zone.boss`, qui peut changer si le farm se réhydrate). */
const pendingBossTrainer = ref<BossTrainer | null>(null)
type PendingBossArgs =
  | { kind: 'normal'; boss: BossTrainer; localDiff: number; genDiffMult: number; gen: number }
  | { kind: 'battleTower'; zoneIndex: number; boss: BossTrainer }
let pendingBossArgs: PendingBossArgs | null = null

function openPendingBossGate(args: PendingBossArgs) {
  const combat = useCombatStore()
  combat.clearTimers()
  if (combat.enemy) combat.bossFailed()
  pendingBossArgs = args
  pendingBossTrainer.value = args.boss
  pendingBoss.value = true
  // Empêche qu’un /load réactive la route de farm pendant la confirmation boss.
  usePlayerStore().extendFarmSessionSuppression()
}

function resetPendingBossGate() {
  pendingBoss.value = false
  pendingBossTrainer.value = null
  pendingBossArgs = null
}

/** Annule l’écran Combattre/Fuir (ex. changement de route farm). */
export function clearPendingBossGate() {
  resetPendingBossGate()
}

function playerBlocksCombatForStarter(): boolean {
  const player = usePlayerStore()
  const gen = player.currentGeneration
  if (!getStartersForGen(gen)) return false
  if (!player.chosenStarters[gen]) return true
  const inventory = useInventoryStore()
  const slug = player.chosenStarters[gen]
  return !inventory.collection.some((p) => p.slug === slug && !p.isShiny)
}

function resolveNormalFrontierBoss(): BossTrainer | undefined {
  const player = usePlayerStore()
  if (player.combatGeneration !== null || player.battleTowerZone != null) return undefined
  return getZone(player.currentGeneration, player.currentZone)?.boss
}

export function useCombatLoop() {
  const combat = useCombatStore()
  const player = usePlayerStore()
  const inventory = useInventoryStore()
  const daycare = useDaycareStore()
  const achievements = useAchievements()
  const progressionMoments = useProgressionMoments()
  const auth = useAuthStore()
  const { addToast } = useToast()
  const audio = useAudioManager()
  const { synergy } = useTeamSynergy()
  const { showDamage, showReward } = useDamageNumbers()

  /** Or / XP réellement attribués au KO (aligné pastilles index + nombres flottants). */
  function computeKillRewardsFromCurrentEnemy(): { gold: number; xp: number } {
    const enemy = combat.enemy
    if (!enemy) return { gold: 0, xp: 0 }

    const towerZone = player.battleTowerZone
    const towerType =
      towerZone != null && towerZone >= 1 && towerZone <= 18
        ? (BATTLE_TOWER_TYPE_ORDER[towerZone - 1] ?? null)
        : null
    const isRepeatTowerBossKill =
      enemy.isBoss &&
      towerType !== null &&
      player.battleTowerDefeatedTypes.includes(towerType)
    const capstoneDefeatedStat =
      Math.max(0, Math.floor(Number((player.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >= 1
    const isRepeatCapstoneBossKill =
      enemy.isBoss &&
      player.battleTowerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX &&
      (player.battleTowerCapstoneDefeated || capstoneDefeatedStat)

    if (isRepeatTowerBossKill || isRepeatCapstoneBossKill) return { gold: 0, xp: 0 }

    const goldPenalty = player.penaltyType === 'gold' ? (1 - player.penaltyPercent / 100) : 1
    const activeCombatGen = player.activeCombatGen
    // Point d'assemblage unique de la pile or : borné par `goldStackCap` (admin),
    // miroir de `resolveSnapshotFarmGoldBonusMult` côté serveur. Sans plafond
    // (valeur 0, défaut) le calcul est identique au comportement historique.
    const bonusGoldMult = player.capCombatGoldStack(
      player.currentZoneMasteryGoldMult
      * (player as any).pokedexGoldMultForGen(activeCombatGen)
      * synergy.value.goldMult
      * player.activeLureGoldMult
      * player.ascensionGoldMult
      * player.combatKeystoneEffect.goldMult
    )

    const gold = Math.max(1, Math.round(enemy.goldReward * goldPenalty * bonusGoldMult))
    const xp = Math.max(1, Math.round(enemy.xpReward * player.ascensionXpMult))
    return { gold, xp }
  }

  const expectedKillRewards = computed(() => computeKillRewardsFromCurrentEnemy())

  // Cache team DPS: recomputed only when enemy types or team composition changes,
  // not on every 1-second tick. Avoids O(team × types) recalculation 60×/min.
  const cachedTeamDps = computed(() => {
    if (!combat.enemy) return 0
    return getEffectiveDps(combat.enemy.types)
  })

  // Canonical power is shared with inventory/raids/expeditions; combat adds region and type context.
  function teamCombatDpsOpts(): TeamCombatDpsOpts {
    return {
      battleTowerZone: player.battleTowerZone,
      isEndgame: player.isEndgame,
      combatGeneration: player.combatGeneration,
      currentGeneration: player.currentGeneration,
      zoneMasteryDpsMult: player.currentZoneMasteryDpsMult,
      pokedexDpsMult: player.pokedexDpsMult,
      synergyDpsMult: synergy.value.dpsMult,
    }
  }

  function getPokeDps(poke: { slug: string; cosmeticFormSlug?: string | null; level: number; stars: number; isShiny: boolean; rarity?: Rarity }, enemyTypes?: PokemonType[]) {
    const power = getPokemonPowerBreakdown(poke)
    const pokeGen = getSlugGeneration(resolvePowerBaseSlug(poke.slug, poke.cosmeticFormSlug))
    const combatGen = player.combatGeneration ?? player.currentGeneration
    const regionMult = player.isEndgame ? 1.0 : (pokeGen >= combatGen ? 1.0 : 0.001)
    const attackerTypes = getPokemonTypes(poke)
    let typeMult = 1
    if (enemyTypes && enemyTypes.length > 0) {
      typeMult = Math.max(...attackerTypes.map(atkType =>
        getTypeEffectiveness(atkType, enemyTypes)
      ))
    }
    typeMult = battleTowerTrialOffenseTypeMult(typeMult, player.battleTowerZone)
    const permanentDps = Math.round(power.power * regionMult)
    const effectiveDps = computePokemonOffensiveDps(
      poke as Parameters<typeof computePokemonOffensiveDps>[0],
      enemyTypes ?? [],
      teamCombatDpsOpts(),
    )
    return {
      baseDmg: power.baseDamage,
      evoMult: power.evolutionMult,
      rarityMult: power.powerTierMult,
      shinyMult: power.shinyMult,
      regionMult,
      typeMult,
      permanentDps,
      effectiveDps,
    }
  }

  function getEffectiveDps(enemyTypes: PokemonType[]): number {
    return computeTeamOffensiveDps(inventory.team, enemyTypes, teamCombatDpsOpts())
  }

  function resolveCombatZone() {
    if (player.battleTowerZone != null) {
      return getBattleTowerZone(player.battleTowerZone)
    }
    return getZone(player.activeCombatGen, player.activeCombatZone)
  }

  function currentZone() {
    return resolveCombatZone()
  }

  function randomWild(): WildPokemon {
    const zone = resolveCombatZone()
    if (!zone || zone.wild.length === 0) {
      return { nameFr: 'Rattata', nameEn: 'Rattata', slug: 'rattata', type: 'normal', baseHp: 30, baseAtk: 6 }
    }
    return zone.wild[Math.floor(Math.random() * zone.wild.length)]!
  }

  function spawnBattleTowerBossCombat(boss: BossTrainer) {
    const isCapstoneZone = player.battleTowerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX
    const capstoneAlreadyDefeated =
      player.battleTowerCapstoneDefeated ||
      Math.max(0, Math.floor(Number((player.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >= 1
    if (isCapstoneZone && capstoneAlreadyDefeated) {
      const nextZone = getNextBattleTowerZoneIndex(player.battleTowerDefeatedTypes, true)
      if (nextZone !== player.battleTowerZone) {
        player.enterBattleTower(nextZone)
        setTimeout(() => spawnEnemy(), 0)
      }
      return
    }

    const bossTypes = boss.types ?? ['normal']
    const timerSec = boss.timerSeconds ?? 30
    const rewardMult = player.ascensionRewardMult
    const isCapstone = isCapstoneZone
    const baseHp = isCapstone ? BATTLE_TOWER_CAPSTONE_BOSS_HP : BATTLE_TOWER_BOSS_HP
    const maxHp = Math.max(
      1,
      Math.round(
        scaleBattleTowerTrialBossHp(
          baseHp,
          player.ascensionLevel,
          player.ascensionUpgrades.enemyMitigation,
        ) * player.combatKeystoneEffect.enemyHpMult,
      ),
    )
    combat.setEnemy({
      nameFr: `Boss : ${boss.nameFr}`,
      nameEn: `Boss: ${boss.nameEn}`,
      slug: boss.slug,
      types: bossTypes,
      spriteUrl: getTrainerSpriteUrl(boss.slug),
      maxHp,
      currentHp: maxHp,
      level: 100,
      goldReward: getBattleTowerTrialBossGold(rewardMult),
      xpReward: getBattleTowerTrialBossXp(rewardMult),
      isBoss: true,
      bossTimerSeconds: timerSec,
      isShiny: false,
    })
    // Pas de son au changement d'ennemi/boss: trop fatigant en sessions longues.
  }

  function spawnBattleTowerFarmWild() {
    const farmZone = getBattleTowerFarmZone()
    const poke = farmZone.wild[Math.floor(Math.random() * farmZone.wild.length)]!
    const gen = 9
    const zoneNum = 13
    const stage = 10
    const localDiff = (zoneNum - 1) * 10 + stage
    const diffScale = 1 + localDiff * 0.01
    const btBonusShinyMult =
      synergy.value.shinyMult
      * player.activeLureShinyMult
      * player.combatKeystoneEffect.shinyMult
      * player.ascensionNgShinyMult
      * player.ascensionRunMutatorEffect.shinyMult
    const isShiny = Math.random() < getShinyRate(player.shinyCharms, player.pokedexMaster) * btBonusShinyMult
    const hp = Math.max(
      1,
      Math.round(BATTLE_TOWER_FARM_WILD_HP * player.ascensionEnemyHpMult * player.combatKeystoneEffect.enemyHpMult)
    )
    const xpBase = Math.round(50 * (1 + (gen - 1) * 0.15))
    // Or calé sur invoc ×50 Paldea (getBattleTowerSanctuaryGoldPerKill)
    const rewardMult = player.ascensionRewardMult
    const goldNonShiny = getBattleTowerSanctuaryGoldPerKill(rewardMult)
    const goldReward = Math.max(
      1,
      isShiny ? Math.round(goldNonShiny * WILD_SHINY_GOLD_MULT) : goldNonShiny,
    )
    const xpReward = Math.max(
      1,
      Math.round(xpBase * diffScale * (isShiny ? WILD_SHINY_XP_MULT : 1) * rewardMult)
    )
    combat.setEnemy({
      nameFr: isShiny ? `✨ ${poke.nameFr} sauvage ✨` : `${poke.nameFr} sauvage`,
      nameEn: isShiny ? `✨ Wild ${poke.nameEn} ✨` : `Wild ${poke.nameEn}`,
      slug: poke.slug,
      types: getPokemonTypes(poke.slug),
      spriteUrl: '',
      maxHp: hp,
      currentHp: hp,
      level: Math.max(80, Math.min(100, Math.round(80 + localDiff * 20 / 130))),
      goldReward,
      xpReward,
      isBoss: false,
      bossTimerSeconds: null,
      isShiny,
    })
    void audio.playPokemonCry(poke.slug)
  }

  function spawnEnemy() {
    if (playerBlocksCombatForStarter()) return
    if (pendingBoss.value) return

    if (player.battleTowerZone != null) {
      const z = player.battleTowerZone
      if (towerTrialPaused.value && isBattleTowerBossTrialZone(z)) {
        return
      }
      if (suppressTowerBossGateOnce && isBattleTowerBossTrialZone(z)) {
        suppressTowerBossGateOnce = false
        return
      }
      if (z >= 1 && z <= 18) {
        // Anti-farm: un boss de tour déjà validé n'est plus rejouable.
        const typeKey = BATTLE_TOWER_TYPE_ORDER[z - 1]
        if (typeKey && player.battleTowerDefeatedTypes.includes(typeKey)) {
          const nextZone = getNextBattleTowerZoneIndex(
            player.battleTowerDefeatedTypes,
            player.battleTowerCapstoneDefeated,
          )
          if (nextZone !== z) {
            player.enterBattleTower(nextZone)
            setTimeout(() => spawnEnemy(), 0)
            return
          }
        }
        const towerZone = getBattleTowerZone(z)
        if (towerZone?.boss) {
          // Confort auto: si activé, bypass l'écran de confirmation en tour.
          if (player.combatAutomation.autoFightBossIfBattable) {
            spawnBattleTowerBossCombat(towerZone.boss)
            return
          }
          openPendingBossGate({ kind: 'battleTower', zoneIndex: z, boss: towerZone.boss })
        }
        return
      }
      if (z === BATTLE_TOWER_CAPSTONE_ZONE_INDEX) {
        const capstoneAlreadyDefeated =
          player.battleTowerCapstoneDefeated ||
          Math.max(0, Math.floor(Number((player.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >= 1
        if (capstoneAlreadyDefeated) {
          const nextZone = getNextBattleTowerZoneIndex(
            player.battleTowerDefeatedTypes,
            true,
          )
          if (nextZone !== z) {
            player.enterBattleTower(nextZone)
            setTimeout(() => spawnEnemy(), 0)
            return
          }
        }
        const towerZone = getBattleTowerZone(z)
        if (towerZone?.boss) {
          if (player.combatAutomation.autoFightBossIfBattable) {
            spawnBattleTowerBossCombat(towerZone.boss)
            return
          }
          openPendingBossGate({ kind: 'battleTower', zoneIndex: z, boss: towerZone.boss })
        }
        return
      }
      if (z === BATTLE_TOWER_FARM_ZONE_INDEX) {
        if (!battleTowerFarmUnlocked(player.battleTowerDefeatedTypes)) {
          const nextZone = getNextBattleTowerZoneIndex(
            player.battleTowerDefeatedTypes,
            player.battleTowerCapstoneDefeated,
          )
          if (nextZone !== z) {
            player.enterBattleTower(nextZone)
            setTimeout(() => spawnEnemy(), 0)
          } else {
            addToast(
              'Termine toutes les épreuves de type pour débloquer la zone suivante.',
              '🔒',
              'warning',
              5000,
            )
          }
          return
        }
        spawnBattleTowerFarmWild()
        return
      }
    }

    if (
      isPaldeaEndgameFrontierSuperseded(
        player.currentGeneration,
        player.currentZone,
        player.defeatedBosses,
        player.battleTowerZone,
        player.combatGeneration,
      ) &&
      (!player.battleTowerLategameDisabled || player.role === 'admin')
    ) {
      player.enterBattleTowerAtProgress()
      setTimeout(() => spawnEnemy(), 0)
      return
    }

    const gen = player.activeCombatGen
    const zone = player.activeCombatZone
    const stage = player.isFarming ? 5 : player.currentStage

    // Local difficulty resets per generation so each region feels fresh
    const localDifficulty = (zone - 1) * 10 + stage

    // Adaptive gen scaling: gen9 champion ≈ 8K DPS, gen1 champion ≈ 4.3K DPS
    // A maxed legendary 5★ team of 6 ≈ 7200 DPS (14K with type effectiveness)
    let genDiffMult = 1 + (gen - 1) * (0.095 + 1.5 / localDifficulty)
    // Gen 6 (Kalos) has a very small pool — reduce wild difficulty
    if (gen === 6) genDiffMult *= KALOS_WILD_GEN_MULT

    if (player.isBossStage) {
      const boss = resolveNormalFrontierBoss() ?? currentZone()?.boss
      if (boss) {
        if (canAutoFightBoss(boss, localDifficulty, genDiffMult, gen)) {
          if (suppressBossAutoRetreatOnce) suppressBossAutoRetreatOnce = false
          spawnBoss(boss, localDifficulty, genDiffMult, gen)
          return
        }

        const skipAutoRetreatThisSpawn = suppressBossAutoRetreatOnce
        if (suppressBossAutoRetreatOnce) suppressBossAutoRetreatOnce = false

        if (
          !skipAutoRetreatThisSpawn &&
          player.combatAutomation.autoRetreatToLastFarmRouteOnBossFailure &&
          player.goToLastSelectedFarmRoute()
        ) {
          setTimeout(() => spawnEnemy(), 0)
          return
        }
        // Porte boss : affichée après reprise frontière (skip ci-dessus) ou si pas de route farm / repli désactivé.
        openPendingBossGate({ kind: 'normal', boss, localDiff: localDifficulty, genDiffMult, gen })
        return
      }
      // Données zone corrompues : éviter un écran vide à l’étape boss.
      console.warn('[COMBAT] isBossStage sans boss — repli étape sauvage', {
        gen: player.currentGeneration,
        zone: player.currentZone,
      })
      player.retreatStage()
      spawnWild(localDifficulty, genDiffMult, gen, zone, stage)
    } else {
      if (suppressBossAutoRetreatOnce) suppressBossAutoRetreatOnce = false
      spawnWild(localDifficulty, genDiffMult, gen, zone, stage)
    }
  }

  function spawnWild(localDiff: number, genDiffMult: number, gen: number, zone: number, stage: number) {
    const poke = randomWild()
    const bonusShinyMult =
      player.currentZoneMasteryShinyMult
      * synergy.value.shinyMult
      * player.activeLureShinyMult
      * player.combatKeystoneEffect.shinyMult
      * player.ascensionNgShinyMult
      * player.ascensionRunMutatorEffect.shinyMult
    const isShiny = Math.random() < getShinyRate(player.shinyCharms, player.pokedexMaster) * bonusShinyMult
    // HP scales with local difficulty (resets per gen) × gentle gen multiplier
    const hp = Math.max(
      1,
      Math.round(
        poke.baseHp * (1 + localDiff * 0.62) * genDiffMult * 2.38
          * player.ascensionEnemyHpMult * player.combatKeystoneEffect.enemyHpMult
      ),
    )
    // Gold: voir getWildGoldReward (aligné boutique bonbons XL)
    const diffScale = 1 + localDiff * 0.01
    // XP scales gently with gen so new-region Pokémon level faster
    const xpBase = Math.round(50 * (1 + (gen - 1) * 0.15))
    const rewardMult = player.ascensionRewardMult
    // Or : variante « courbe d'ascension admin » ; XP : multiplicateur brut inchangé.
    const goldReward = Math.max(
      1,
      Math.round(getWildGoldReward(gen, zone, stage, isShiny) * player.ascensionRewardGoldMult),
    )
    const xpReward = Math.max(
      1,
      Math.round(xpBase * diffScale * (isShiny ? WILD_SHINY_XP_MULT : 1) * rewardMult)
    )
    combat.setEnemy({
      nameFr: isShiny ? `✨ ${poke.nameFr} sauvage ✨` : `${poke.nameFr} sauvage`,
      nameEn: isShiny ? `✨ Wild ${poke.nameEn} ✨` : `Wild ${poke.nameEn}`,
      slug: poke.slug,
      types: getPokemonTypes(poke.slug),
      spriteUrl: '',
      maxHp: hp,
      currentHp: hp,
      level: Math.max(3, Math.min(95, Math.round(3 + localDiff * 92 / 130))),
      goldReward,
      xpReward,
      isBoss: false,
      bossTimerSeconds: null,
      isShiny,
    })
    void audio.playPokemonCry(poke.slug)
  }

  function spawnBoss(boss: BossTrainer, localDiff: number, _genDiffMult: number, gen: number) {
    const totalHp = computeZoneBossTotalHp(
      gen,
      player.activeCombatZone,
      player.ascensionEnemyHpMult * player.combatKeystoneEffect.enemyHpMult,
    )
    // Boss rewards: same exponential curve as wilds (×10), puis scale global or
    const diffScale = 1 + localDiff * 0.01
    const wildBase =
      gen === 1
        ? WILD_GOLD_BASE_GEN1
        : Math.round(WILD_GOLD_BASE_GEN2 * Math.pow(WILD_GOLD_GEN_EXPONENT, gen - 2))
    const goldBase = wildBase * 10
    const xpBase = Math.round(500 * (1 + (gen - 1) * 0.15))
    const bossTypes = boss.types ?? getPokemonTypes(boss.team[0]?.slug ?? 'normal')
    combat.setEnemy({
      nameFr: `Boss : ${boss.nameFr}`,
      nameEn: `Boss: ${boss.nameEn}`,
      slug: boss.slug,
      types: bossTypes,
      spriteUrl: getTrainerSpriteUrl(boss.slug),
      maxHp: totalHp,
      currentHp: totalHp,
      level: Math.max(5, Math.min(100, Math.round(5 + localDiff * 95 / 130))),
      goldReward: Math.max(
        1,
        Math.round(
          goldBase
            * diffScale
            * player.ascensionRewardGoldMult
            * COMBAT_GOLD_REWARD_SCALE
            * wildGoldFactor(),
        ),
      ),
      xpReward: Math.max(1, Math.round(xpBase * diffScale * player.ascensionRewardMult)),
      isBoss: true,
      bossTimerSeconds: boss.timerSeconds,
    })
    // Pas de son au lancement boss: évite les pics sonores répétitifs.
  }

  function estimateBossRequiredDps(boss: BossTrainer, _localDiff: number, _genDiffMult: number, gen: number) {
    const timerSec = boss.timerSeconds ?? 30
    return zoneBossMinTeamDps(
      gen,
      player.activeCombatZone,
      timerSec,
      player.ascensionEnemyHpMult * player.combatKeystoneEffect.enemyHpMult,
    )
  }

  function canAutoFightBoss(
    boss: BossTrainer,
    localDiff: number,
    genDiffMult: number,
    gen: number,
  ): boolean {
    if (!player.combatAutomation.autoFightBossIfBattable) return false
    const bossTypes = boss.types ?? getPokemonTypes(boss.team[0]?.slug ?? 'normal')
    const dpsPenalty = player.penaltyType === 'dps' ? (1 - player.penaltyPercent / 100) : 1
    return isBossBattableByTeam(
      inventory.team,
      bossTypes,
      estimateBossRequiredDps(boss, localDiff, genDiffMult, gen),
      {
        ...teamCombatDpsOpts(),
        dpsPenaltyMult: dpsPenalty,
        ascensionDpsMult: player.ascensionDpsMult,
        combatPressureMult: player.combatAscensionPressureMult,
        keystoneDpsMult:
          player.combatKeystoneEffect.dpsMult * player.ascensionRunMutatorEffect.dpsMult,
        goldWellDpsMult: player.goldWellDpsMult,
        transcendenceDpsMult: player.ascensionTranscendenceDpsMult,
      },
    )
  }

  function applyCapstoneVictoryIfNeeded(wasBoss: boolean) {
    if (!wasBoss || player.battleTowerZone !== BATTLE_TOWER_CAPSTONE_ZONE_INDEX) return
    if (player.battleTowerCapstoneDefeated) return
    progressionMoments.onCapstoneDefeated()
    trackProgressionEvent('capstone_defeated')
    player.markBattleTowerCapstoneDefeated()
    void auth.saveGameState(false, true)
    player.enterBattleTower(
      getNextBattleTowerZoneIndex(player.battleTowerDefeatedTypes, true),
    )
  }

  function checkEnemyDeath() {
    if (combat.pausedByVisibility) return
    // isDying / killCreditPending : empêche re-entrée pendant l’anim (et un 2ᵉ crédit).
    if (!combat.isEnemyDead || !combat.enemy || combat.isDying || killCreditPending) return

    const towerZone = player.battleTowerZone
    const towerType =
      towerZone != null && towerZone >= 1 && towerZone <= 18
        ? (BATTLE_TOWER_TYPE_ORDER[towerZone - 1] ?? null)
        : null
    const isRepeatTowerBossKill =
      combat.enemy.isBoss &&
      towerType !== null &&
      player.battleTowerDefeatedTypes.includes(towerType)
    const capstoneDefeatedStat =
      Math.max(0, Math.floor(Number((player.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >= 1
    const isRepeatCapstoneBossKill =
      combat.enemy.isBoss &&
      player.battleTowerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX &&
      (player.battleTowerCapstoneDefeated || capstoneDefeatedStat)

    const { gold: goldReward, xp: xpReward } = computeKillRewardsFromCurrentEnemy()
    const wasBoss = combat.enemy.isBoss

    if (isRepeatTowerBossKill) {
      addToast('Boss de tour déjà vaincu : aucune récompense.', '🛡️', 'warning', 3500)
    } else if (isRepeatCapstoneBossKill) {
      addToast('Red déjà vaincu : aucune récompense.', '🛡️', 'warning', 3500)
    }

    // Persister tout de suite : un reload offline ne doit pas réinitialiser la victoire.
    applyCapstoneVictoryIfNeeded(wasBoss)

    // Zone kill tracking (hors tour de combat et boss)
    if (player.battleTowerZone == null && !combat.enemy.isBoss) {
      const gen = player.activeCombatGen
      const zone = player.activeCombatZone
      player.addStat(`kills_g${gen}z${zone}`, 1)
    }

    // Décrémenter le leurre actif
    player.consumeLureKill()

    // ── Death animation sequence ────────────────────────────────────────────
    // Stop attacks, start the dying visual, show floating reward numbers
    combat.clearTimers()
    killCreditPending = true
    dyingStartedAt = Date.now()
    combat.setDying(true)

    if (goldReward > 0) {
      accrueCombatGold(goldReward, { isShinyKill: Boolean(combat.enemy?.isShiny) })
    }

    // Compter le KO tout de suite (avec l’or) — sinon farm-sync peut arriver pendant
    // l’anim de mort avec un buffer or sans killDelta → crédit 0 / purge fantôme.
    player.recordKill()

    if (xpReward > 0) {
      player.addXp(xpReward)
    }

    const team = inventory.team
    if (team.length > 0 && xpReward > 0) {
      const pokemonXpReward = wasBoss ? xpReward : xpReward * 4
      const xpDivisor = 1 + (team.length - 1) * 0.4
      const xpPerPokemon = Math.max(1, Math.floor(pokemonXpReward / xpDivisor))
      for (const poke of team) {
        inventory.addPokemonXp(poke.id, xpPerPokemon, player.currentGeneration)
      }
    }

    // Floating reward numbers — far sides so they don't overlap the sprite
    if (goldReward > 0) showReward(goldReward, 'gold', { x: 14, y: 58 })
    if (xpReward > 0)   showReward(xpReward,  'xp',   { x: 86, y: 58 })

    // After animation completes, spawn the next foe in-place (no « Recherche » gap).
    onDeathAnimComplete = () => {
      onDeathAnimComplete = null
      deathAnimTimeout = null
      dyingStartedAt = 0
      killCreditPending = false
      combat.setDying(false)
      combat.finalizeEncounterKill()

      if (wasBoss) {
        audio.playSfx('bossDefeated')
      } else if (player.audioPreferences.wildKillSfxEnabled) {
        audio.playSfx('enemyDefeated')
      }

      if (
        wasBoss &&
        player.battleTowerZone != null &&
        player.battleTowerZone >= 1 &&
        player.battleTowerZone <= 18
      ) {
        const t = BATTLE_TOWER_TYPE_ORDER[player.battleTowerZone - 1]
        if (t) {
          player.recordBattleTowerBossWin(t)
          void auth.saveGameState(false, true)
          player.enterBattleTower(
            getNextBattleTowerZoneIndex(
              player.battleTowerDefeatedTypes,
              player.battleTowerCapstoneDefeated,
            ),
          )
        }
      }

      if (!player.isFarming && player.battleTowerZone == null) {
        if (wasBoss) {
          player.advanceStage()
          player.resolveZoneVictoryAutomation()
          // Persister tout de suite la clé boss (ex. g1z13) avant passage Johto + loadGameState au starter.
          void auth.saveGameState(false, true)
        } else {
          player.addStageKill()
        }
      }

      achievements.checkAchievementsImmediate()
      spawnEnemy()
      if (combat.enemy?.currentHp != null && combat.enemy.currentHp <= 0) {
        combat.killEnemy()
      }
    }

    if (deathAnimTimeout != null) clearTimeout(deathAnimTimeout)
    deathAnimTimeout = setTimeout(() => {
      onDeathAnimComplete?.()
    }, DEATH_ANIM_MS)
  }

  /** Tick de dégâts (registre module — survit à combat.reset()). */
  function installOverrideAutoAttack() {
    const tick = () => {
      if (combat.pausedByVisibility) return
      if (pendingBoss.value) return
      if (!combat.enemy || combat.enemy.currentHp <= 0) return
      const baseDps = cachedTeamDps.value
      if (baseDps <= 0) return
      const dpsPenalty = player.penaltyType === 'dps' ? (1 - player.penaltyPercent / 100) : 1
      const effectiveDps = computeCombatEffectiveDps(baseDps, {
        dpsPenaltyMult: dpsPenalty,
        ascensionDpsMult: player.ascensionDpsMult,
        combatPressureMult: player.combatAscensionPressureMult,
        goldWellDpsMult: player.goldWellDpsMult,
        transcendenceDpsMult: player.ascensionTranscendenceDpsMult,
        keystoneDpsMult:
          player.combatKeystoneEffect.dpsMult * player.ascensionRunMutatorEffect.dpsMult,
      })
      combat.enemy.currentHp = Math.max(0, combat.enemy.currentHp - effectiveDps)
      combat.triggerHitFlash()
      if (effectiveDps / combat.enemy.maxHp > 0.15) combat.triggerBigHitFlash()
      showDamage(effectiveDps, { x: 45 + Math.random() * 10, y: 30 + Math.random() * 20 })
      daycare.addDamage(effectiveDps)
      player.addStat('totalDamage', effectiveDps)
      player.addStat('playTimeSeconds', 1)
      checkEnemyDeath()

      if (combat.enemy?.isBoss && combat.bossTimeRemaining !== null && combat.bossTimeRemaining > 0) {
        combat.tickBossTimerAfterDamage()
        if (combat.bossTimeRemaining !== null && combat.bossTimeRemaining <= 0 && combat.enemy.currentHp > 0) {
          combat.bossFailed()
          audio.playSfx('bossFailed')
          if (player.battleTowerZone == null) {
            player.handleNormalBossFailure()
          }
          setTimeout(() => spawnEnemy(), 1000)
        }
      }
    }
    registerCombatDamageTick(tick)
    combat.overrideAutoAttack = tick
  }

  function init() {
    installOverrideAutoAttack()
    if (!initialized) {
      initialized = true
    }

    if (!combat.enemy && !pendingBoss.value) {
      spawnEnemy()
    } else {
      combat.ensureAttackLoop()
    }
  }

  function confirmBoss() {
    if (!pendingBossArgs) return
    if (pendingBossArgs.kind === 'battleTower') {
      const { boss } = pendingBossArgs
      resetPendingBossGate()
      clearTowerTrialPause()
      spawnBattleTowerBossCombat(boss)
      return
    }
    const { boss, localDiff, genDiffMult, gen } = pendingBossArgs
    resetPendingBossGate()
    spawnBoss(boss, localDiff, genDiffMult, gen)
  }

  function retreatFromBattleTowerTrial() {
    clearPendingBossGate()
    combat.bossFailed()
    audio.playSfx('bossFailed')
    pauseBattleTowerTrial()
  }

  function resumeTowerTrial() {
    clearTowerTrialPause()
    suppressTowerBossGateOnce = false
    spawnEnemy()
  }

  function declineBoss() {
    if (!pendingBossArgs) return
    if (pendingBossArgs.kind === 'battleTower') {
      retreatFromBattleTowerTrial()
      return
    }
    resetPendingBossGate()
    combat.clearTimers()
    combat.bossFailed()
    audio.playSfx('bossFailed')
    if (player.battleTowerZone == null) {
      player.handleNormalBossFailure()
    }
    setTimeout(() => spawnEnemy(), 400)
  }

  function forceBossSpawn(genId: number, zoneId: number) {
    const zone = getZone(genId, zoneId)
    if (!zone) return
    const boss = zone.boss
    const localDifficulty = (zoneId - 1) * 10 + 10
    let genDiffMult = 1 + (genId - 1) * (0.095 + 1.5 / localDifficulty)
    if (genId === 6) genDiffMult *= KALOS_WILD_GEN_MULT
    spawnBoss(boss, localDifficulty, genDiffMult, genId)
  }

  return {
    init,
    spawnEnemy,
    checkEnemyDeath,
    getEffectiveDps,
    getPokeDps,
    currentZone,
    forceBossSpawn,
    pendingBoss,
    pendingBossTrainer,
    towerTrialPaused,
    clearPendingBossGate,
    clearTowerTrialPause,
    pauseTowerTrialForRouteChange,
    confirmBoss,
    declineBoss,
    retreatFromBattleTowerTrial,
    resumeTowerTrial,
    expectedKillRewards,
    suppressBossAutoRetreatOnNextSpawn,
  }
}

/**
 * Répare une boucle de combat figée sans F5 (timers arrêtés, tick perdu après reset,
 * pause onglet incohérente, animation de mort bloquée).
 * Ne rejoue jamais le crédit or/XP d’un KO déjà traité.
 */
export function repairCombatLoop() {
  const combat = useCombatStore()
  const loop = useCombatLoop()

  loop.init()

  if (typeof document !== 'undefined' && !document.hidden && combat.pausedByVisibility) {
    combat.resumeFromVisibility()
    return
  }

  if (combat.isDying && combat.isEnemyDead) {
    const elapsed = dyingStartedAt > 0 ? Date.now() - dyingStartedAt : Number.POSITIVE_INFINITY
    // Anim normale en cours : ne pas toucher (sinon double crédit or/XP + flottants).
    if (elapsed < DEATH_ANIM_MS + DEATH_STUCK_GRACE_MS) {
      return
    }

    const player = usePlayerStore()
    const capstoneDefeated =
      player.battleTowerCapstoneDefeated ||
      Math.max(0, Math.floor(Number((player.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >= 1
    const isRepeatCapstoneBoss =
      combat.enemy?.isBoss &&
      player.battleTowerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX &&
      capstoneDefeated

    if (deathAnimTimeout != null) {
      clearTimeout(deathAnimTimeout)
      deathAnimTimeout = null
    }

    if (isRepeatCapstoneBoss) {
      clearDeathAnimationState()
      combat.setDying(false)
      combat.killEnemy()
      return
    }

    // Anim bloquée après le crédit : terminer le spawn sans re-créditer.
    if (onDeathAnimComplete) {
      onDeathAnimComplete()
      return
    }

    clearDeathAnimationState()
    combat.setDying(false)
    return
  }

  if (!combat.enemy) {
    if (towerTrialPaused.value) return
    if (!pendingBoss.value) {
      loop.spawnEnemy()
    }
    return
  }

  if (combat.pausedByVisibility || combat.isDying) return

  if (combat.isEnemyDead) {
    loop.checkEnemyDeath()
    return
  }

  combat.ensureAttackLoop()
}
